import { useState } from "react";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAY_LABELS = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
const TIME_SLOTS = [
  "09:00 am","09:30 am","10:00 am","10:30 am",
  "11:00 am","11:30 am","12:00 pm","12:30 pm",
  "01:00 pm","01:30 pm","02:00 pm","02:30 pm",
];
const TIMEZONES = [
  "🌐 America - New York (UTC -04:00)",
  "🌐 America - Chicago (UTC -06:00)",
  "🌐 America - Denver (UTC -07:00)",
  "🌐 America - Los Angeles (UTC -07:00)",
  "🌐 Europe - London (UTC +01:00)",
  "🌐 Europe - Paris (UTC +02:00)",
  "🌐 Europe - Berlin (UTC +02:00)",
  "🌐 Asia - Dubai (UTC +04:00)",
  "🌐 Asia - Kolkata (UTC +05:30)",
  "🌐 Asia - Singapore (UTC +08:00)",
  "🌐 Asia - Tokyo (UTC +09:00)",
  "🌐 Pacific - Auckland (UTC +12:00)",
  "🌐 Australia - Sydney (UTC +10:00)"

];

export default function CalendarPicker({ onSubmit }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timezone, setTimezone] = useState(TIMEZONES[0]);
  const [submitted, setSubmitted] = useState(false);

  function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
  function firstDayOfMonth(y, m) { return new Date(y, m, 1).getDay(); }

  function prevMonth() {
    if (submitted) return;
    setSelectedDate(null);
    setSelectedTime(null);
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  }

  function nextMonth() {
    if (submitted) return;
    setSelectedDate(null);
    setSelectedTime(null);
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  }

  function selectDay(d) {
    if (submitted) return;
    const dt = new Date(viewYear, viewMonth, d);
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (dt < todayMidnight) return;
    setSelectedDate(new Date(viewYear, viewMonth, d));
    setSelectedTime(null);
  }

  function selectTime(t) {
    if (submitted) return;
    setSelectedTime(t);
  }

  function handleSubmit() {
    if (!selectedDate || !selectedTime || submitted) return;
    setSubmitted(true);
    onSubmit(selectedDate, selectedTime);
  }

  const days = daysInMonth(viewYear, viewMonth);
  const firstDay = firstDayOfMonth(viewYear, viewMonth);
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div className={`bg-white border border-slate-200 rounded-2xl p-4 shadow-sm w-full ${submitted ? "opacity-60 pointer-events-none" : ""}`}>
      {/* Meta */}
      <div className="flex items-center gap-4 mb-3">
        <span className="flex items-center gap-1.5 text-xs text-slate-500">
          <span>⏱</span> 30 minutes
        </span>
        <span className="flex items-center gap-1.5 text-xs text-slate-500">
          <span>🌐</span> Eastern Time (UTC -04:00)
        </span>
      </div>

      {/* Month Nav */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-800 text-sm">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h4>
        <div className="flex gap-1">
          <button
            onClick={prevMonth}
            className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors text-sm font-bold"
          >
            ‹
          </button>
          <button
            onClick={nextMonth}
            className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors text-sm font-bold"
          >
            ›
          </button>
        </div>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-slate-400 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: days }).map((_, i) => {
          const d = i + 1;
          const dt = new Date(viewYear, viewMonth, d);
          const isPast = dt < todayMidnight;
          const isToday =
            d === today.getDate() &&
            viewMonth === today.getMonth() &&
            viewYear === today.getFullYear();
          const isSelected =
            selectedDate &&
            selectedDate.getDate() === d &&
            selectedDate.getMonth() === viewMonth &&
            selectedDate.getFullYear() === viewYear;

          return (
            <button
              key={d}
              onClick={() => selectDay(d)}
              disabled={isPast}
              className={`text-center text-xs py-1.5 rounded-lg transition-all duration-150 font-medium
                ${isPast ? "text-slate-300 cursor-not-allowed" : "cursor-pointer"}
                ${isSelected ? "bg-blue-600 text-white" : ""}
                ${isToday && !isSelected ? "border border-blue-500 text-blue-600" : ""}
                ${!isPast && !isSelected ? "hover:bg-blue-50 hover:text-blue-600 text-slate-700" : ""}
              `}
            >
              {d}
            </button>
          );
        })}
      </div>

      {/* Timezone */}
      <select
        value={timezone}
        onChange={(e) => setTimezone(e.target.value)}
        className="mt-3 w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-600 bg-white focus:outline-none focus:border-blue-400 cursor-pointer"
      >
        {TIMEZONES.map((tz) => (
          <option key={tz} value={tz}>{tz}</option>
        ))}
      </select>

      {/* Time slots */}
      {selectedDate && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
            Available times
          </p>
          <div className="grid grid-cols-2 gap-2">
            {TIME_SLOTS.map((t) => (
              <button
                key={t}
                onClick={() => selectTime(t)}
                className={`border rounded-xl py-2 text-xs font-medium transition-all duration-150
                  ${selectedTime === t
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50"
                  }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Continue */}
      <button
        onClick={handleSubmit}
        disabled={!selectedDate || !selectedTime || submitted}
        className="mt-4 w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-150
          bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]
          disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
      >
        Continue →
      </button>
    </div>
  );
}